import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Route, Switch, Redirect, useLocation } from 'react-router-dom';
import GetUserAuthData from 'user-profile/actions/GetUserAuthDataAction';
import ChangeEmailConfirmationPage from 'user-profile/components/ChangeEmailConfirmationPage';
import ChangeEmailVerificationPage from 'user-profile/components/ChangeEmailVerificationPage';
import { USER_PROFILE_SET_LOADING } from 'user-profile/enums/UserProfileTypes';
import {
  MatomoProvider,
  createInstance,
  useMatomo,
} from '@datapunt/matomo-tracker-react';
import { useMaintenance } from 'app/context/MaintenanceContext';
import './assets/scss/style.scss';

const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse" />
  </div>
);

// Containers
const TheLayout = React.lazy(() =>
  import('app/components/containers/TheLayout')
);

const Login = React.lazy(() => import('auth/components/LoginForm'));
const ResetPassword = React.lazy(() =>
  import('auth/components/ResetPasswordForm')
);
const SubmitResetPasswordForm = React.lazy(() =>
  import('auth/components/SubmitResetPasswordForm')
);
const RegisterOrganisation = React.lazy(() =>
  import('organisations/components/RegisterOrganisation')
);
const InvitedUserForm = React.lazy(() =>
  import('user/components/InvitedUserForm')
);

const WorkosCallback = React.lazy(() =>
  import('user/components/WorkosCallback')
);

const Page404 = React.lazy(() => import('app/public/Page404'));
const Page500 = React.lazy(() => import('app/public/Page500'));
const MaintenancePage = React.lazy(() => import('app/public/MaintenancePage'));

const IocAggregateChartEmailScreenshot = React.lazy(() =>
  import('app/reports/screenshot/IocAggregateChart')
);

const IocReportData = React.lazy(() => import('app/reports/IocReport'));

// Component to handle health check for public routes
const PublicRouteWithHealthCheck = ({ component: Component, ...rest }) => {
  const { performHealthCheck, setIsMaintenanceMode } = useMaintenance();
  const location = useLocation();

  useEffect(() => {
    // Reset maintenance mode when entering public route (will be set again if health check fails)
    setIsMaintenanceMode(false);
    
    // Perform quick health check for public routes with short timeout (3 seconds)
    const checkHealth = async () => {
      const isHealthy = await performHealthCheck(3000);
      if (!isHealthy) {
        window.location.href = '/maintenance';
      }
    };

    // Small delay to ensure component renders first
    const timeoutId = setTimeout(() => {
      checkHealth();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname, performHealthCheck, setIsMaintenanceMode]);

  return <Component {...rest} />;
};

const App = () => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.userProfile);
  const { performHealthCheck, isMaintenanceMode } = useMaintenance();

  const instance =
    process.env.NODE_ENV === 'production'
      ? createInstance({
          urlBase: 'https://everyangle.matomo.cloud/',
          siteId: 2,
        })
      : null;

  // Initial health check on app load (skip for public routes and maintenance)
  useEffect(() => {
    const initialHealthCheck = async () => {
      const currentPath = window.location.pathname;
      
      // Skip health check for maintenance page
      if (currentPath === '/maintenance') {
        dispatch({ type: USER_PROFILE_SET_LOADING, action: false });
        return;
      }

      // Skip health check for public routes - they will check themselves
      if (
        currentPath === '/login' ||
        currentPath.includes('/pub/') ||
        currentPath.includes('/auth/workos/callback') ||
        currentPath === '/reset' ||
        currentPath.startsWith('/reset/')
      ) {
        // For public routes, just set loading to false and let them check themselves
        dispatch({ type: USER_PROFILE_SET_LOADING, action: false });
        return;
      }

      // For protected routes, perform health check
      const isHealthy = await performHealthCheck();
      if (!isHealthy) {
        window.location.href = '/maintenance';
        return;
      }

      // If health check passes, proceed with normal app initialization
      /* Load Authenticated User Data */
      dispatch(GetUserAuthData());
    };

    initialHealthCheck();
  }, [dispatch, performHealthCheck]);

  useEffect(() => {
    if (userData?.data?.first_name) {
      // eslint-disable-next-line no-underscore-dangle, no-multi-assign
      const _paq = (window._paq = window._paq || []);
      _paq.push([
        'setUserId',
        `${userData?.data?.first_name} ${userData?.data?.last_name} - ${userData?.data?.organisation?.name}`,
      ]);
    }
  }, [userData?.data]);

  // Check if we're on maintenance page to bypass loading spinner
  const isMaintenancePage = window.location.pathname === '/maintenance';

  return (
    <>
      <MatomoProvider value={instance}>
        {userData.loading && !isMaintenancePage ? (
          <div className="loader">
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : (
          <BrowserRouter>
            <React.Suspense fallback={loading}>
              <Switch>
                <Route
                  exact
                  path="/maintenance"
                  name="Maintenance Page"
                  render={(props) => <MaintenancePage {...props} />}
                />
                <Route
                  exact
                  path="/login"
                  name="Login Page"
                  render={(props) => (
                    <PublicRouteWithHealthCheck
                      component={Login}
                      {...props}
                    />
                  )}
                />
                <Route
                  exact
                  path="/reset"
                  name="Reset Password Page"
                  render={(props) => (
                    <PublicRouteWithHealthCheck
                      component={ResetPassword}
                      {...props}
                    />
                  )}
                />
                <Route
                  exact
                  path="/reset/email/:email"
                  name="Reset Password Page"
                  render={(props) => (
                    <PublicRouteWithHealthCheck
                      component={ResetPassword}
                      {...props}
                    />
                  )}
                />
                <Route
                  exact
                  path="/pub/reset/token/:token/email/:email"
                  name="Reset Password Page"
                  render={(props) => (
                    <PublicRouteWithHealthCheck
                      component={SubmitResetPasswordForm}
                      {...props}
                    />
                  )}
                />
                <Route
                  exact
                  path="/pub/verify-change-email/token/:token/email/:email"
                  name="Change Email Verification Page"
                  render={(props) => (
                    <PublicRouteWithHealthCheck
                      component={ChangeEmailVerificationPage}
                      {...props}
                    />
                  )}
                />
                <Route
                  exact
                  path="/pub/change-email/token/:token/email/:email"
                  name="Change Email Page"
                  render={(props) => (
                    <PublicRouteWithHealthCheck
                      component={ChangeEmailConfirmationPage}
                      {...props}
                    />
                  )}
                />
                <Route
                  exact
                  path="/pub/register/token/:token/email/:email"
                  name="Organisation and User Register Page"
                  render={(props) => (
                    <PublicRouteWithHealthCheck
                      component={RegisterOrganisation}
                      {...props}
                    />
                  )}
                />
                <Route
                  exact
                  path="/pub/active-invited-user/token/:token/email/:email"
                  name="Organisation and User Register Page"
                  render={(props) => (
                    <PublicRouteWithHealthCheck
                      component={InvitedUserForm}
                      {...props}
                    />
                  )}
                />
                <Route
                  exact
                  path="/auth/workos/callback"
                  name="Workos Callback Page"
                  render={(props) => (
                    <PublicRouteWithHealthCheck
                      component={WorkosCallback}
                      {...props}
                    />
                  )}
                />

                <Route
                  exact
                  path="/pub/screenshot/ioc-aggregate-chart/:token/:encrypted"
                  name="Screenshot Chart"
                  render={(props) => (
                    <IocAggregateChartEmailScreenshot {...props} />
                  )}
                />
                <Route
                  exact
                  path="/pub/report/ioc-dashboard/:token/:encrypted"
                  name="Screenshot Chart"
                  render={(props) => <IocReportData {...props} />}
                />
                <Route
                  exact
                  path="/404"
                  name="Page 404"
                  render={(props) => <Page404 {...props} />}
                />
                <Route
                  exact
                  path="/500"
                  name="Page 500"
                  render={(props) => <Page500 {...props} />}
                />
                <Route
                  path="/"
                  name="Home"
                  render={(props) => {
                    // Redirect to maintenance if in maintenance mode
                    if (isMaintenanceMode) {
                      return <Redirect to="/maintenance" />;
                    }
                    return <TheLayout {...props} />;
                  }}
                />
              </Switch>
            </React.Suspense>
          </BrowserRouter>
        )}
      </MatomoProvider>
    </>
  );
};

export default App;
